import { useEffect, useState } from 'react';
import heic2any from 'heic2any';
import { getExtension } from '../util/fileType';

const HEIC_EXTENSIONS = new Set(['heic', 'heif', 'heifs', 'heic-sequence']);

/** True when the source is a HEIC/HEIF that no browser can decode natively. */
export const isHeicSource = (src?: string, override?: string): boolean =>
  HEIC_EXTENSIONS.has(getExtension(src || '', override));

/** One transcode per URL — a card re-renders every time its parent state changes. */
const cache = new Map<string, Promise<string>>();

const MAX_CACHED = 20;

const evictOldest = () => {
  const oldest = cache.keys().next().value as string | undefined;
  if (oldest === undefined) return;
  const pending = cache.get(oldest);
  cache.delete(oldest);
  // The entry holds an object URL, so drop it with the cache or it leaks for the
  // lifetime of the tab.
  void pending?.then((url) => URL.revokeObjectURL(url)).catch(() => undefined);
};

const transcode = (src: string): Promise<string> => {
  let pending = cache.get(src);
  if (!pending) {
    pending = (async () => {
      const res = await fetch(src);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const converted = await heic2any({
        blob: await res.blob(),
        toType: 'image/jpeg',
        quality: 0.92,
      });
      const blob = Array.isArray(converted) ? converted[0] : converted;
      if (!blob) throw new Error('heic2any returned no image');
      return URL.createObjectURL(blob);
    })();
    // Don't let one broken file poison the cache.
    pending.catch(() => cache.delete(src));
    cache.set(src, pending);
    if (cache.size > MAX_CACHED) evictOldest();
  }
  return pending;
};

/**
 * A URL an <img> can actually render.
 *
 * Non-HEIC sources pass straight through untouched, so this is safe to call
 * unconditionally. HEIC is transcoded to JPEG via heic2any; on failure it falls
 * back to the original source and lets the caller's existing onError handling
 * take over.
 */
export const useDisplayableSrc = (
  src?: string,
  override?: string,
): { src: string; isConverting: boolean } => {
  const original = src || '';
  const [displaySrc, setDisplaySrc] = useState(original);
  const [isConverting, setIsConverting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!original || !isHeicSource(original, override)) {
      setDisplaySrc(original);
      setIsConverting(false);
      return;
    }

    setIsConverting(true);
    transcode(original).then(
      (converted) => {
        if (cancelled) return;
        setDisplaySrc(converted);
        setIsConverting(false);
      },
      () => {
        if (cancelled) return;
        setDisplaySrc(original);
        setIsConverting(false);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [original, override]);

  return { src: displaySrc, isConverting };
};
